import TicketCuticle from '@/components/manage/TicketCuticle';
import PriceDisplay from '@/components/PriceDisplay';
import { TransText } from '@/i18n/trans/text';
import type { GetVouchersResponseDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import { format } from '@/utils/dateTime';
import {
  Badge,
  Icons,
  ProgressBar,
  Typography,
} from '@packages/webshop-design-system';
import type { FC } from 'react';

type VoucherItem = NonNullable<GetVouchersResponseDTO['items']>[number];

interface VoucherSummaryProps {
  voucher: VoucherItem;
}

function getVoucherStatus(voucher: VoucherItem) {
  if (voucher.isCancelled) return 'cancelled';
  if (voucher.isUsed) return 'used';
  if (!voucher.isValid) return 'expired';
  return null;
}

const VoucherSummary: FC<VoucherSummaryProps> = ({ voucher }) => {
  const validUntil = voucher.validUntil;
  const validUntilDay = validUntil && format(validUntil, 'dd MMM');
  const validUntilYear = validUntil && format(validUntil, 'yyyy');

  const voucherStatus = getVoucherStatus(voucher);

  return (
    <div
      className={cn(
        'flex h-[150px] w-full flex-row rounded-xl bg-white shadow-md',
        voucherStatus && 'opacity-75 grayscale'
      )}
    >
      <div
        className={cn(
          'flex flex-1 flex-col rounded-l-xl rounded-tr-none border border-r-0 border-neutral-pale',
          'gap-2 pb-2.5 pl-4 pr-2 pt-4'
        )}
      >
        {voucher.type?.name && (
          <div className="inline-flex w-fit items-start gap-2 rounded bg-primary-light p-1 px-2">
            <Typography
              variant="label"
              className="text-center font-bold text-primary"
            >
              {voucher.type.name}
            </Typography>
          </div>
        )}

        {/* Balance / nominal value show for history vouchers too — the
            status (used / expired / cancelled) is conveyed by the badge and
            grayscale, not by hiding the amounts (BR-51922). */}
        <div
          className={cn(
            'flex flex-1 flex-col gap-1.5',
            !voucher.type?.name && 'justify-center'
          )}
        >
          <Typography variant="label" className="font-bold">
            <TransText i18nKey="remainingBalance" />
          </Typography>
          <div className="flex items-baseline gap-1">
            <Typography
              variant="heading1"
              className="shrink-0 text-right font-extrabold text-primary"
            >
              <PriceDisplay amount={voucher.balance} />
            </Typography>

            <div className="flex flex-1 items-start gap-0.5 text-xs leading-none tracking-tight text-neutral">
              <span className="leading-4">
                <Typography variant="body2">/</Typography>
              </span>
              <span className="leading-4">
                <Typography variant="body1">
                  <PriceDisplay amount={voucher.nominalValue} />
                </Typography>
              </span>
            </div>
          </div>

          <div>
            <ProgressBar
              data-testid="progressbar"
              value={voucher.balance}
              maxValue={voucher.nominalValue}
              aria-hidden
              className={{ root: 'h-[5px] max-w-52' }}
            />
          </div>
        </div>

        <div className="mt-auto flex items-center gap-1">
          <Typography variant="body2" className="my-1 text-neutral">
            {voucher.code}
          </Typography>
          <div className="size-4" />
        </div>
      </div>

      <div className="relative isolate h-[160px]">
        <div className="absolute inset-y-0 left-1/2 -z-10 w-[90%] -translate-x-1/2 bg-background" />
        <TicketCuticle
          isVertical
          dashLength={5}
          dashColor="#E9EAF1"
          className={{
            root: 'h-[150px]',
            line: 'my-1.5',
            dot: 'border-neutral-pale',
            dotStart:
              'bg-transparent shadow-[inset_0_-1px_2px_rgba(0,0,0,0.1)]',
            dotEnd: 'bg-transparent shadow-[inset_0_3px_3px_rgba(0,0,0,0.12)]',
          }}
        />
      </div>

      <div
        className={cn(
          'flex w-[120px] flex-col justify-between gap-3 rounded-r-xl px-2 py-4 pr-4 pt-5',
          'border border-l-0 border-neutral-pale'
        )}
      >
        {voucherStatus && (
          <Badge className="w-fit gap-1" variant="disabled">
            <Icons.cancel width={16} height={16} />
            <Typography variant="label">
              <TransText i18nKey={voucherStatus} />
            </Typography>
          </Badge>
        )}
        <div className="flex flex-1 flex-col justify-center gap-2">
          <div className="flex flex-col items-start justify-center gap-1">
            <Typography variant="body2" className="text-neutral">
              <TransText i18nKey="validUntil" />:
            </Typography>
          </div>
          <div className="flex flex-col items-start justify-center gap-1">
            <div className="flex flex-wrap items-baseline gap-1 text-wrap text-right text-xs leading-4 text-dark">
              {validUntilDay ? (
                <>
                  <span className="text-sm font-bold tracking-tight">
                    {validUntilDay},
                  </span>
                  <span className="text-sm font-medium tracking-tight">
                    {validUntilYear}
                  </span>
                </>
              ) : (
                '—'
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherSummary;
